import ApiService from '@/api';
import { ProjectModel, ResponseModel } from '@/models';
import { CurrentUser, UserRoleEnum } from 'tir-pm-user-core';
import {
  AddTaskZmkForUserRequest,
  Events,
  GetOrderedTasksRequestModel,
  GetOrderedTasksResponseModel,
  GetProjectTasksRequestModel,
  OrderTaskModel,
  PaginationRequestModel,
  PaginationResponseModel,
  RemoveTaskZmkForUserRequest,
  TaskFilterModel,
  TeammateModel,
  ZmkTaskByPerformerListRequest,
  ZmkTaskByPerformerListResponse,
} from '../models';
import GetEmployeesDetailForManagerListRequest from '@/api/Main/dataSource/api/projects/performers/i-service/model/request/GetEmployeesDetailForManagerListRequest';
import GetEmployeesDetailForManagerListResponse from '@/api/Main/dataSource/api/projects/performers/i-service/model/response/GetEmployeesDetailForManagerListResponse';
import GetOwnedDetailUsersResponse from '@/api/Main/dataSource/api/projects/performers/i-service/model/response/GetOwnedDetailUsersResponse';
import GetOwnedDetailUsersRequest from '@/api/Main/dataSource/api/projects/performers/i-service/model/request/GetOwnedDetailUsersRequest';
import EnumUserStatus from '@/api/Main/dataSource/api/users/base/enums/EnumUserStatus';
import {
  mapGetPerformerProjectsRequest,
  mapGetPerformerProjectsResponse,
  mapGetTasksByProjectIdRequest,
  mapGetTasksByProjectIdResponse,
  mapGetTeamResponse,
  mapSubscribeOrderListRequest,
  mapUnSubscribeOrderListRequest,
} from './TasksOrderPagesAdapterServiceMappers';
import { TaskModel, TaskPersonModel, TaskQueueModel } from '@/components/tasks-kanban/models';
import GetOrderedTaskOverviewListRequest from '../models/GetOrderedTaskOverviewListRequestModel';
import { TaskParameterModel } from '@/components';
import ZmkTaskPerformerModel from '../models/ZmkTaskPerformerModel';
import { NamespaceModel } from '@/components/sidebar/models';
import PageResponse from '../models/PageResponseModel';
import {
  mapGetProjectRequest,
  mapGetProjectResponse,
} from '@/layouts/project-tasks-layout/adapter/AdapterServiceMappers';
import WsResponseModelT from '@/api/File/dataSource/api/schema-builder/server/models/WsResponseModelT';
import UpsertTaskOrderForUserEventResponse from '@/api/Main/dataSource/api/projects/task/i-service/models/task-order/response/UpsertTaskOrderForUserEventResponse';
import SubscribeToOrderTaskListRequestModel from '../models/SubscribeToOrderTaskListRequestModel';
import UnSubscribeToOrderTaskListRequestModel from '../models/UnSubscribeToOrderTaskListRequestModel';

/**Сервис для обработки api запросов */
export class TasksOrderPagesAdapterService {
  /**Сервис для использования api */
  private readonly api = new ApiService();
  /**Событие обновления списка очередности задач */
  private OnUpsertTaskOrderForUserEvent = new Events<number>();

  constructor() {
    this.api.Main.TaskOrderSaveService.OnUpsertTaskZmkForUserEvent.on(
      this.onUpsertTaskOrderForUser,
    );
  }

  /**Коллбек при событии обновления списка очередности задач */
  private onUpsertTaskOrderForUser = (
    response: WsResponseModelT<UpsertTaskOrderForUserEventResponse>,
  ) => {
    if (response.IsSuccess) {
      this.OnUpsertTaskOrderForUserEvent.trigger(response.Value.PerformerId);
    }
  };
  /**Отписка от события обновления порядка задач */
  unSubscribeOrderPages() {
    this.api.Main.TaskOrderSaveService.OnUpsertTaskZmkForUserEvent.removeListener(
      this.onUpsertTaskOrderForUser,
    );
  }
  /**Получение пользователей команды */
  getTeam() {
    return new Promise<ResponseModel<TeammateModel[]>>(async (resolve) => {
      let result = new ResponseModel<TeammateModel[]>();

      const isOwner = CurrentUser.Roles.some((x) => x == UserRoleEnum.Owner);
      const query = isOwner
        ? this.api.Main.PerformersListService.getOwnedDetailUsersAsync(
            new GetOwnedDetailUsersRequest({
              Search: '',
              ProjectId: null,
              StatusList: [EnumUserStatus.Active],
            }),
          )
        : this.api.Main.EmployeeService.getEmployeesDetailForManagerListAsync(
            new GetEmployeesDetailForManagerListRequest(),
          );
      query
        .then(
          (response: GetEmployeesDetailForManagerListResponse | GetOwnedDetailUsersResponse) => {
            result = mapGetTeamResponse(response);
            return resolve(result);
          },
        )
        .catch(() => {
          result.IsSuccess = false;
          return resolve(result);
        });
    });
  }
  /**
   * Получить список задач по проекту
   * @param request Запрос
   * @returns
   */
  getTasksByProjectId(
    request: GetProjectTasksRequestModel,
  ): Promise<PaginationResponseModel<OrderTaskModel[]>> {
    return new Promise<PaginationResponseModel<OrderTaskModel[]>>(async (resolve) => {
      let result = new PaginationResponseModel<OrderTaskModel[]>();

      await this.api.Main.ProjectTaskGetService.getTaskListByTreeShortAsync(
        mapGetTasksByProjectIdRequest(request),
      )
        .then((resp) => {
          result = mapGetTasksByProjectIdResponse(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getTasksByProjectId', e);
          return resolve(result);
        });
    });
  }
  /**
   * * Получение списка задач ЗМК
   */
  getZmkTaskByPerformerList(
    request: ZmkTaskByPerformerListRequest,
  ): Promise<ResponseModel<ZmkTaskByPerformerListResponse>> {
    return new Promise<ResponseModel<ZmkTaskByPerformerListResponse>>(async (resolve) => {
      let result = new ResponseModel<ZmkTaskByPerformerListResponse>();

      await this.api.Main.TaskOrderGetService.getZmkTaskByPerformerListAsync(request)
        .then((resp) => {
          result.Value = new ZmkTaskByPerformerListResponse({
            PerformerPage: resp.PerformerPage,
            TaskPerformerList: resp.TaskPerformerList.map(
              (taskPerformer) =>
                new ZmkTaskPerformerModel({
                  Performer: taskPerformer.Performer,
                  PageStep: request.TaskPage?.Take ?? 15,
                  TaskList: taskPerformer.TaskList.map(
                    (task) =>
                      new OrderTaskModel({
                        ...task,
                        Id: task.TaskId,
                        Name: task.Name,
                        HasAccessToTask: task.HasAccessToTask,
                        NamespaceInfo: [
                          new NamespaceModel({
                            Id: task.Namespace.Id,
                            Color: task.Namespace.Color,
                            IconUrl: task.Namespace.IconUrl,
                            Name: task.Namespace.Name,
                          }),
                        ],
                        Project: new ProjectModel({
                          Id: task.Project.Id,
                          ImageUrl: task.Project.ImageUrl,
                          Name: task.Project.Name,
                          PreviewColor: task.Project.PreviewColor,
                        }),
                        Queues: [
                          new TaskQueueModel({
                            Id: task.QueueTask.Id,
                            Name: task.QueueTask.Name,
                            NamespaceId: task.QueueTask.NamespaceId,
                          }),
                        ],
                        Status: new TaskParameterModel({
                          Color: task.Status.StatusColor,
                          Name: task.Status.StatusName,
                          Id: task.Status.StatusId,
                          Description: task.Status.StatusDescription,
                        }),

                        ProjectId: task.Project.Id,
                        IsHighlighted: task.IsInQueue,
                        Difficulty: task.Difficulty,
                        Priority: new TaskParameterModel({
                          Id: task?.Priority?.PriorityId,
                          Color: task?.Priority?.PriorityColor,
                          Name: task?.Priority?.PriorityName,
                        }),
                        WorkTime: task.WorkTime,
                        EstimateTime: task.EstimateTime,
                      }),
                  ),
                  TaskPage: new PageResponse({
                    Size: taskPerformer.TaskPage.Size,
                    Count: taskPerformer.TaskPage.Count,
                    Current: taskPerformer.TaskPage.Current,
                  }),
                }),
            ),
          });
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getTasksByProjectId', e);
          return resolve(result);
        });
    });
  }

  /**Добавление задачи в очередность */
  addTaskZmkForUserAsync(request: AddTaskZmkForUserRequest): Promise<ResponseModel<Boolean>> {
    return new Promise<ResponseModel<Boolean>>(async (resolve) => {
      let result = new ResponseModel<Boolean>();

      await this.api.Main.TaskOrderSaveService.addTaskZmkForUserAsync(request)
        .then((resp) => {
          result.Value = resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR addTaskZmkForUserAsync', e);
          return resolve(result);
        });
    });
  }
  /**
   * * Удаление задачи змк для пользователя в очередность
   */
  removeTaskZmkForUserAsync(request: RemoveTaskZmkForUserRequest): Promise<ResponseModel<Boolean>> {
    return new Promise<ResponseModel<Boolean>>(async (resolve) => {
      let result = new ResponseModel<Boolean>();

      await this.api.Main.TaskOrderSaveService.removeTaskZmkForUserAsync(request)
        .then((resp) => {
          result.Value = resp;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR addTaskZmkForUserAsync', e);
          return resolve(result);
        });
    });
  }

  /**
   * Получить кол-во задач в проекте
   * @param projectId ID проекта
   * @param filter Фильтр
   * @returns
   */
  getCountTasksByProjectId(projectId: number, filter: TaskFilterModel) {
    return new Promise<ResponseModel<number>>(async (resolve) => {
      let result = new ResponseModel<number>();

      const request = new GetProjectTasksRequestModel({
        ProjectId: projectId,
        Page: new PaginationRequestModel({
          Skip: 0,
          Take: 1,
        }),
        Filter: filter,
      });

      await this.api.Main.ProjectTaskGetService.getTaskListByTreeShortAsync(
        mapGetTasksByProjectIdRequest(request),
      )
        .then((resp) => {
          result.Value = resp.Page.Count;
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getCountTasksByProjectId', e);
          return resolve(result);
        });
    });
  }
  /**
   * Получить проект
   * @param projectId ID проекта
   * @returns
   */
  getProject(projectId: number) {
    return new Promise<ResponseModel<ProjectModel>>(async (resolve) => {
      const result = new ResponseModel<ProjectModel>();

      await this.api.Main.ProjectBaseService.getIdAsync(mapGetProjectRequest(projectId))
        .then((resp) => {
          result.Value = mapGetProjectResponse(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getProject', e);
          return resolve(result);
        });
    });
  }
  /**
   * Получить список проектов исполнителя
   * @param performerId ID исполнителя
   * @returns
   */
  getPerformerProjects(performerId: number) {
    return new Promise<ResponseModel<ProjectModel[]>>(async (resolve) => {
      const result = new ResponseModel<ProjectModel[]>();

      await this.api.Main.ProjectBaseService.searchAsync(
        mapGetPerformerProjectsRequest(performerId),
      )
        .then((resp) => {
          result.Value = mapGetPerformerProjectsResponse(resp);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR getPerformerProjects', e);
          return resolve(result);
        });
    });
  }

  /**
   * Подписаться на событие изменения списка очередности задач
   * @param userId ID пользователя
   * @param callback Коллбек функция
   * @returns
   */
  subscribeOrderList(userId: number, callback: (performerId: number) => void) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.TaskOrderSaveService.subscribeToOrderTaskAsync(
        mapSubscribeOrderListRequest(userId),
      )
        .then((resp) => {
          result.Value = !!resp;
          result.IsSuccess = !!resp;

          if (resp) this.OnUpsertTaskOrderForUserEvent.on(callback);

          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR subscribeOrderList', e);
          return resolve(result);
        });
    });
  }
  /**
   * Отписаться от события изменения списка очередности задач
   * @param userId ID пользователя
   * @param callback Коллбек функция
   * @returns
   */
  unSubscribeOrderList(userId: number, callback: () => void) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.TaskOrderSaveService.unSubscribeToOrderTaskAsync(
        mapUnSubscribeOrderListRequest(userId),
      )
        .then((resp) => {
          result.Value = !!resp;
          result.IsSuccess = !!resp;

          this.OnUpsertTaskOrderForUserEvent.removeListener(callback);

          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR unSubscribeOrderList', e);
          return resolve(result);
        });
    });
  }
  /**Подписаться на событие изменения списка очередности задач для нескольких юзеров */
  subscribeToOrderTaskList(
    req: SubscribeToOrderTaskListRequestModel,
    callback: (performerId: number) => void,
  ) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.TaskOrderSaveService.subscribeToOrderTaskListAsync(req)
        .then((resp) => {
          if (resp) this.OnUpsertTaskOrderForUserEvent.on(callback);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR unSubscribeOrderList', e);
          return resolve(result);
        });
    });
  }
  /**Отписка от событие изменения списка очередности задач для нескольких юзеров */
  unSubscribeToOrderTaskList(
    req: UnSubscribeToOrderTaskListRequestModel,
    callback: (performerId: number) => void,
  ) {
    return new Promise<ResponseModel<boolean>>(async (resolve) => {
      const result = new ResponseModel<boolean>();

      await this.api.Main.TaskOrderSaveService.unSubscribeToOrderTaskListAsync(req)
        .then((resp) => {
          if (resp) this.OnUpsertTaskOrderForUserEvent.removeListener(callback);
          return resolve(result);
        })
        .catch((e) => {
          result.IsSuccess = false;
          result.ErrorCode = e?.ErrorCode;
          result.ErrorMessage = e?.ErrorMessage;
          console.error('ERROR unSubscribeOrderList', e);
          return resolve(result);
        });
    });
  }
}
